<template>
  <div class="settings-page">
    <title-bar :title="$lang.settings_title || 'SETTINGS'" />

    <div class="settings-content">
      <div v-if="readError" class="settings-read-error">
        {{ readError }}
      </div>
      <div v-if="!readError && emptyState" class="settings-empty-state">
        {{ $lang.settings_empty_state || 'No account security data available yet.' }}
      </div>

      <!-- Account Security -->
      <section class="settings-section">
        <div class="settings-section__header">
          <svg class="section-icon" viewBox="0 0 24 24" fill="currentColor"><path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z"/></svg>
          <h2>{{ $lang.settings_account_security || 'ACCOUNT SECURITY' }}</h2>
        </div>
        <div class="settings-card">
          <div class="settings-item settings-item--border" @click="$jumpTo('/passwordLogin')">
            <div class="settings-item__info">
              <p class="settings-item__label">{{ $lang.mine_txt9 || 'Change Login Password' }}</p>
              <p class="settings-item__value">{{ $lang.settings_password_tip || 'Update your login password' }}</p>
            </div>
            <button class="settings-item__btn">
              {{ $lang.settings_change || 'CHANGE' }}
            </button>
          </div>
          <div
            class="settings-item settings-item--border"
            @click="goWithdrawPassword"
          >
            <div class="settings-item__info">
              <p class="settings-item__label">{{ $lang.mine_txt10 || 'Change Withdrawal Password' }}</p>
              <p class="settings-item__value">{{ withdrawPasswordStatus }}</p>
            </div>
            <button class="settings-item__btn">
              {{ privacyPasswdSetted === 'yes' ? ($lang.settings_change || 'CHANGE') : ($lang.settings_set || 'SET') }}
            </button>
          </div>
          <div
            class="settings-item settings-item--border"
            :class="{ 'settings-item--inactive': isPhoneBound }"
            @click="goPhoneSecurity"
          >
            <div class="settings-item__info">
              <p class="settings-item__label">{{ $lang.editProfile_phone || 'Phone' }}</p>
              <p class="settings-item__value">{{ phoneStatus }}</p>
            </div>
            <button
              class="settings-item__btn"
              :class="{ 'settings-item__btn--gray': isPhoneBound }"
              :disabled="isPhoneBound"
            >
              {{ isPhoneBound ? ($lang.settings_bound || 'BOUND') : ($lang.settings_bind || 'BIND') }}
            </button>
          </div>
          <div class="settings-item" @click="goBankCard">
            <div class="settings-item__info">
              <p class="settings-item__label">{{ $lang.bank_title || 'Bank Card' }}</p>
              <p class="settings-item__value">{{ bankStatus }}</p>
            </div>
            <button class="settings-item__btn">
              {{ $lang.settings_manage || 'MANAGE' }}
            </button>
          </div>
        </div>
      </section>

      <!-- Preferences -->
      <section class="settings-section">
        <div class="settings-section__header">
          <svg class="section-icon" viewBox="0 0 24 24" fill="currentColor"><path d="M19.14 12.94c.04-.3.06-.61.06-.94 0-.32-.02-.64-.07-.94l2.03-1.58a.49.49 0 00.12-.61l-1.92-3.32a.488.488 0 00-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54a.484.484 0 00-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.05.3-.07.62-.07.94s.02.64.07.94l-2.03 1.58a.49.49 0 00-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6A3.6 3.6 0 1115.6 12 3.611 3.611 0 0112 15.6z"/></svg>
          <h2>{{ $lang.settings_preferences || 'PREFERENCES' }}</h2>
        </div>
        <div class="settings-card">
          <!-- Language (functional) -->
          <div class="settings-item settings-item--border" @click="showLangPopup = true">
            <div class="settings-item__left">
              <div class="settings-item__icon-round">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="#9aa4b2"><path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zm6.93 6h-2.95a15.65 15.65 0 00-1.38-3.56A8.03 8.03 0 0118.92 8zM12 4.04c.83 1.2 1.48 2.53 1.91 3.96h-3.82c.43-1.43 1.08-2.76 1.91-3.96zM4.26 14C4.1 13.36 4 12.69 4 12s.1-1.36.26-2h3.38c-.08.66-.14 1.32-.14 2s.06 1.34.14 2H4.26zm.82 2h2.95c.32 1.25.78 2.45 1.38 3.56A7.987 7.987 0 015.08 16zm2.95-8H5.08a7.987 7.987 0 014.33-3.56A15.65 15.65 0 008.03 8zM12 19.96c-.83-1.2-1.48-2.53-1.91-3.96h3.82c-.43 1.43-1.08 2.76-1.91 3.96zM14.34 14H9.66c-.09-.66-.16-1.32-.16-2s.07-1.35.16-2h4.68c.09.65.16 1.32.16 2s-.07 1.34-.16 2zm.25 5.56c.6-1.11 1.06-2.31 1.38-3.56h2.95a8.03 8.03 0 01-4.33 3.56zM16.36 14c.08-.66.14-1.32.14-2s-.06-1.34-.14-2h3.38c.16.64.26 1.31.26 2s-.1 1.36-.26 2h-3.38z"/></svg>
              </div>
              <span class="settings-item__name">{{ $lang.settings_language || 'Language' }}</span>
            </div>
            <div class="settings-item__right">
              <span class="settings-item__hint">{{ currentLangLabel }}</span>
              <van-icon name="arrow" size="14" color="var(--vk-t2)" />
            </div>
          </div>

          <!-- Push Notifications -->
          <div
            class="settings-item settings-item--border"
            role="switch"
            :aria-checked="String(pushNotifications)"
            @click="togglePushNotifications"
          >
            <div class="settings-item__left">
              <div class="settings-item__icon-round">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="#9aa4b2"><path d="M12 22c1.1 0 2-.9 2-2h-4a2 2 0 002 2zm6-6v-5c0-3.07-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.63 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z"/></svg>
              </div>
              <span class="settings-item__name">{{ $lang.settings_push || 'Push Notifications' }}</span>
            </div>
            <div class="settings-toggle">
              <div
                class="settings-toggle__track"
                :class="{ 'settings-toggle__track--on': pushNotifications }"
              >
                <div class="settings-toggle__thumb"></div>
              </div>
            </div>
          </div>

          <!-- Sound Volume -->
          <div class="settings-item">
            <div class="settings-item__left">
              <div class="settings-item__icon-round">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="#9aa4b2"><path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3A4.5 4.5 0 0014 8.14v7.72c1.48-.73 2.5-2.25 2.5-3.86zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/></svg>
              </div>
              <span class="settings-item__name">{{ $lang.settings_sound || 'Sound Volume' }}</span>
            </div>
            <div class="settings-slider">
              <input
                v-model.number="soundVolume"
                type="range"
                min="0"
                max="100"
                class="settings-slider__input"
                :aria-label="$lang.settings_sound || 'Sound Volume'"
                @input="updateSoundVolume"
              />
            </div>
          </div>
        </div>
      </section>

      <!-- Logout -->
      <div class="settings-logout">
        <button class="settings-logout__btn" @click="showLogoutPopup = true">
          {{ $lang.mine_txt14 || 'Log Out' }}
        </button>
      </div>
    </div>

    <!-- Language Popup -->
    <van-popup v-model="showLangPopup" position="bottom" round class="lang-popup">
      <div class="lang-popup__content">
        <div class="lang-popup__header">
          <span>{{ $lang.settings_language || 'Language' }}</span>
          <van-icon name="cross" size="20" color="var(--vk-t2)" @click="showLangPopup = false" />
        </div>
        <div
          v-for="lang in langOptions"
          :key="lang.value"
          class="lang-popup__item"
          :class="{ 'lang-popup__item--active': currentLang === lang.value }"
          @click="switchLang(lang.value)"
        >
          <span>{{ lang.label }}</span>
          <van-icon v-if="currentLang === lang.value" name="success" size="18" color="#ff5a1f" />
        </div>
      </div>
    </van-popup>

    <!-- Logout Popup -->
    <van-popup v-model="showLogoutPopup" round :close-on-click-overlay="true" class="logout-popup">
      <div class="logout-popup__content">
        <p class="logout-popup__title">{{ $lang.mine_txt4 }}</p>
        <div class="logout-popup__actions">
          <button class="logout-popup__btn logout-popup__btn--cancel" @click="showLogoutPopup = false">
            {{ $lang.Cancelar }}
          </button>
          <button class="logout-popup__btn logout-popup__btn--confirm" @click="confirmLogout">
            {{ $lang.Confirmar }}
          </button>
        </div>
      </div>
    </van-popup>
  </div>
</template>

<script>
import { Init, Logout } from '@/api/common'
import { contentOf, ensureLogin as ensureMineLogin, failMessage, handleNeedLogin, isConfirmedLogout, isOk } from './safeFlow'
import { safeGetStorageItem, safeLocalStorage, safeRemoveStorageItem, safeSetStorageItem } from '@/utils/safeStorage'

function settingsFailureMessage(error, fallback) {
  const message = failMessage(error, fallback)
  return /^network error$/i.test(String(message || '').trim()) ? fallback : message
}

function settingsWriteFailureMessage(error, fallback) {
  const message = failMessage(error, fallback)
  return /^(ok|success|successful|succeed|succeeded|done|saved|complete|completed)$/i.test(String(message || '').trim()) ? fallback : message
}

function settingsLogoutFailureMessage(error, fallback) {
  const message = settingsFailureMessage(error, fallback)
  return settingsWriteFailureMessage({ message }, fallback)
}

function isReadOk(res) {
  return isOk(res)
}

const PUSH_NOTIFICATIONS_PREF_KEY = 'h5SettingsPushNotifications'
const SOUND_VOLUME_PREF_KEY = 'h5SettingsSoundVolume'
const DEFAULT_SOUND_VOLUME = 70

function normalizePushPreference(value) {
  return value === true || /^(on|true|1|yes)$/i.test(String(value || '').trim())
}

function normalizeSoundVolume(value) {
  const number = Number(value)
  if (!Number.isFinite(number)) return DEFAULT_SOUND_VOLUME
  return Math.min(100, Math.max(0, Math.round(number)))
}

function reloadLocationSafely() {
  try {
    const location = typeof window !== 'undefined' && window ? window.location : null
    if (location && typeof location.reload === 'function') {
      location.reload()
      return true
    }
  } catch (e) {
    return false
  }
  return false
}

function getLocalStorage() {
  try {
    return typeof safeLocalStorage === 'function' ? safeLocalStorage() : null
  } catch (e) {
    return null
  }
}

function isEmptySettingsProfile(content) {
  return !(content.phone || content.phoneNo || content.realName || content.privacyPasswdSetted === 'yes')
}

export default {
  name: 'Settings',
  data() {
    const pushNotifications = normalizePushPreference(safeGetStorageItem(getLocalStorage(), PUSH_NOTIFICATIONS_PREF_KEY, 'off'))
    const soundVolume = normalizeSoundVolume(safeGetStorageItem(getLocalStorage(), SOUND_VOLUME_PREF_KEY, String(DEFAULT_SOUND_VOLUME)))
    return {
      showLogoutPopup: false,
      showLangPopup: false,
      profileLoading: false,
      phone: '',
      privacyPasswdSetted: '',
      realName: '',
      readError: '',
      emptyState: false,
      currentLang: safeGetStorageItem(getLocalStorage(), 'defaultLanguage', 'en'),
      pushNotifications,
      persistedPushNotifications: pushNotifications,
      soundVolume,
      persistedSoundVolume: soundVolume,
      langOptions: [
        { label: 'Français', value: 'fr' },
        { label: 'English', value: 'en' }
      ]
    }
  },
  computed: {
    currentLangLabel() {
      const found = this.langOptions.find(l => l.value === this.currentLang)
      return found ? found.label : 'English'
    },
    isPhoneBound() {
      return Boolean(this.phone)
    },
    phoneStatus() {
      if (this.profileLoading) return this.$lang.common_loading || 'Loading...'
      return this.maskPhone(this.phone) || this.$lang.settings_not_bound || 'Not Bound'
    },
    withdrawPasswordStatus() {
      if (this.profileLoading) return this.$lang.common_loading || 'Loading...'
      return this.privacyPasswdSetted === 'yes'
        ? (this.$lang.settings_set || 'Set')
        : (this.$lang.settings_not_set || 'Not Set')
    },
    bankStatus() {
      if (this.profileLoading) return this.$lang.common_loading || 'Loading...'
      return this.realName || this.$lang.authName_title || 'Real name required'
    }
  },
  mounted() {
    if (!this.ensureLogin()) return
    this.loadProfile()
  },
  methods: {
    ensureLogin() {
      return ensureMineLogin(this)
    },
    maskPhone(phone) {
      const value = String(phone || '')
      if (value.length <= 4) return value
      return `${value.slice(0, 3)}****${value.slice(-4)}`
    },
    resetProfileReadState() {
      this.phone = ''
      this.privacyPasswdSetted = ''
      this.realName = ''
      this.emptyState = true
    },
    async loadProfile() {
      if (!this.ensureLogin()) return
      this.profileLoading = true
      try {
        const data = await Init()
        if (handleNeedLogin(this, data, this.$lang.login_txt11 || this.$lang.Entrar || 'Login Now', { isValue: 'isLogin' }, { replace: true })) {
          this.readError = failMessage(data, this.$lang.service_unavailable || 'Service temporarily unavailable. Please try again later.')
          return
        }
        if (isReadOk(data)) {
          const content = contentOf(data)
          this.readError = ''
          this.emptyState = isEmptySettingsProfile(content)
          this.phone = content.phone || content.phoneNo || ''
          this.privacyPasswdSetted = content.privacyPasswdSetted === 'yes' ? 'yes' : 'no'
          this.realName = content.realName || ''
        } else {
          const message = failMessage(data, this.$lang.settings_read_failed || this.$lang.profile_read_failed || 'Account security data could not be loaded. Existing settings have been kept.')
          this.readError = message
          this.$toast({ message, icon: 'cross' })
        }
      } catch (e) {
        const message = settingsFailureMessage(e, this.$lang.settings_read_failed || this.$lang.profile_read_failed || 'Account security data could not be loaded. Existing settings have been kept.')
        this.readError = message
        this.$toast({ message, icon: 'cross' })
      } finally {
        this.profileLoading = false
      }
    },
    goWithdrawPassword() {
      if (!this.ensureLogin()) return
      this.$jumpTo('/setPassWord', { from: 'profile' })
    },
    goPhoneSecurity() {
      if (!this.ensureLogin() || this.isPhoneBound) return
      this.$jumpTo('/phoneUnbound')
    },
    goBankCard() {
      if (!this.ensureLogin()) return
      if (this.realName) {
        this.$jumpTo('/bank')
      } else {
        this.$jumpTo('/authName', { from: 'profile' })
      }
    },
    switchLang(lang) {
      this.currentLang = lang
      const storage = getLocalStorage()
      safeSetStorageItem(storage, 'defaultLanguage', lang)
      // 标记用户已主动选语言 → router applySingleCountry 不再把语言纠回基准(单国模式下切换才能保持)
      safeSetStorageItem(storage, 'langUserSet', '1')
      this.showLangPopup = false
      reloadLocationSafely()
    },
    localPreferenceSaveFailed() {
      if (typeof this.$toast !== 'function') return
      const lang = this.$lang || {}
      this.$toast({
        message: lang.settings_local_preference_error || 'Local setting could not be saved on this device.',
        icon: 'cross'
      })
    },
    persistLocalPreference(key, value) {
      const saved = safeSetStorageItem(getLocalStorage(), key, value)
      if (!saved) this.localPreferenceSaveFailed()
      return saved
    },
    togglePushNotifications() {
      const nextValue = !this.pushNotifications
      if (this.persistLocalPreference(PUSH_NOTIFICATIONS_PREF_KEY, nextValue ? 'on' : 'off')) {
        this.pushNotifications = nextValue
        this.persistedPushNotifications = nextValue
      } else {
        this.pushNotifications = this.persistedPushNotifications
      }
    },
    updateSoundVolume(event) {
      const nextValue = normalizeSoundVolume(event && event.target ? event.target.value : event)
      this.soundVolume = nextValue
      if (!this.persistLocalPreference(SOUND_VOLUME_PREF_KEY, String(nextValue))) {
        this.soundVolume = this.persistedSoundVolume
      } else {
        this.persistedSoundVolume = nextValue
      }
    },
    async confirmLogout() {
      try {
        const data = await Logout()
        if (isConfirmedLogout(data)) {
          this.showLogoutPopup = false
          safeRemoveStorageItem(getLocalStorage(), 'token')
          this.$jumpTo('/home', {}, { replace: true })
          setTimeout(() => { reloadLocationSafely() }, 200)
        } else {
          this.$toast({ message: settingsLogoutFailureMessage(data, this.$lang.logout_failed || this.$lang.service_unavailable || 'Logout request failed. Please try again later.'), icon: 'cross' })
        }
      } catch (e) {
        this.$toast({ message: settingsLogoutFailureMessage(e, this.$lang.logout_failed || 'Logout request failed. Please try again later.'), icon: 'cross' })
      }
    }
  }
}
</script>

<style lang="less" scoped>
@settings-bg: @vk-bg;       /* 页面底 */
@settings-card: @vk-surface;/* 卡片面 */
@settings-neon: @brand;     /* 绿→火焰橙(强调/激活) */
@settings-muted: @vk-t2;    /* 次文字 */
@settings-border: rgba(255, 255, 255, 0.06);
@settings-gray-btn: #333945;/* 中性灰按钮 */

.settings-page {
  min-height: 100vh;
  background: @settings-bg;
  padding-bottom: 40px;
}

.settings-content {
  padding: 12px 16px;
}

.settings-read-error {
  margin-bottom: 12px;
  padding: 10px 12px;
  border-radius: 8px;
  background: rgba(220, 38, 38, 0.12);
  border: 1px solid rgba(220, 38, 38, 0.28);
  color: #f87171;
  font-size: var(--vk-fs-xs);
  line-height: 18px;
}

.settings-empty-state {
  margin-bottom: 12px;
  padding: 10px 12px;
  border-radius: 8px;
  background: rgba(148, 163, 184, 0.12);
  border: 1px solid rgba(148, 163, 184, 0.24);
  color: #cbd5e1;
  font-size: var(--vk-fs-xs);
  line-height: 18px;
}

// ====== SECTION HEADER ======
.settings-section {
  margin-bottom: 20px;

  &__header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 10px;
    padding-left: 2px;

    .section-icon {
      width: 18px;
      height: 18px;
      color: @settings-neon;
      flex-shrink: 0;
    }

    h2 {
      font-size: var(--vk-fs-xs);
      font-weight: 700;
      color: @settings-neon;
      text-transform: uppercase;
      letter-spacing: 2px;
      margin: 0;
    }
  }
}

// ====== CARD ======
.settings-card {
  background: @settings-card;
  border-radius: 12px;
  border: 1px solid @settings-border;
  overflow: hidden;
}

// ====== ITEM ======
.settings-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px;
  cursor: pointer;

  &--border {
    border-bottom: 1px solid @settings-border;
  }

  &--inactive {
    cursor: default;
  }

  &__info {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  &__label {
    font-size: var(--vk-fs-sm);
    color: @settings-muted;
    margin: 0;
  }

  &__value {
    font-size: var(--vk-fs-base);
    color: #ffffff;
    font-weight: 600;
    margin: 0;
  }

  &__left {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  &__right {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  &__icon {
    width: 36px;
    height: 36px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;

    .social-svg {
      width: 20px;
      height: 20px;
    }
  }

  &__icon-round {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.06);
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__name {
    font-size: var(--vk-fs-base);
    color: #ffffff;
    font-weight: 500;
  }

  &__hint {
    font-size: var(--vk-fs-sm);
    color: @settings-muted;
  }

  &__btn {
    background: @settings-neon;
    color: @settings-bg;
    border: none;
    cursor: pointer;
    font-size: var(--vk-fs-xs);
    font-weight: 700;
    padding: 10px 16px;
    border-radius: 10px;
    text-transform: uppercase;
    letter-spacing: 1px;

    &:disabled {
      cursor: default;
    }

    &--gray {
      background: @settings-gray-btn;
      color: #ffffff;
    }
  }
}

// ====== TOGGLE ======
.settings-toggle {
  &__track {
    width: 40px;
    height: 22px;
    border-radius: 11px;
    background: @settings-gray-btn;
    position: relative;
    transition: background 0.2s;

    &--on {
      background: @settings-neon;

      .settings-toggle__thumb {
        left: 20px;
      }
    }
  }

  &__thumb {
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: #ffffff;
    position: absolute;
    top: 2px;
    left: 2px;
    transition: left 0.2s;
  }
}

// ====== SLIDER ======
.settings-slider {
  width: 120px;

  &__input {
    -webkit-appearance: none;
    appearance: none;
    width: 100%;
    height: 4px;
    border-radius: 2px;
    background: @settings-gray-btn;
    outline: none;
    cursor: pointer;

    &::-webkit-slider-thumb {
      -webkit-appearance: none;
      appearance: none;
      width: 16px;
      height: 16px;
      border-radius: 50%;
      background: @settings-neon;
      cursor: pointer;
    }

    &::-moz-range-thumb {
      width: 16px;
      height: 16px;
      border-radius: 50%;
      background: @settings-neon;
      border: none;
      cursor: pointer;
    }
  }
}

// ====== LOGOUT BUTTON ======
.settings-logout {
  padding: 0 0;
  margin-top: 28px;

  &__btn {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    background: rgba(220, 38, 38, 0.1);
    border: 1px solid rgba(220, 38, 38, 0.3);
    border-radius: 12px;
    padding: 14px;
    font-size: var(--vk-fs-15);
    font-weight: 700;
    color: #dc2626;
    cursor: pointer;
    text-transform: uppercase;
    letter-spacing: 1px;
  }
}

// ====== LANGUAGE POPUP ======
.lang-popup {
  background: @settings-card !important;

  &__content {
    padding: 0;
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 18px 20px;
    border-bottom: 1px solid @settings-border;

    span {
      font-size: var(--vk-fs-md);
      font-weight: 700;
      color: #ffffff;
    }
  }

  &__item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px;
    border-bottom: 1px solid @settings-border;
    cursor: pointer;
    transition: background 0.15s;

    span {
      font-size: var(--vk-fs-15);
      color: #ffffff;
    }

    &:last-child {
      border-bottom: none;
    }

    &:active {
      background: rgba(255, 255, 255, 0.04);
    }

    &--active {
      background: rgba(255, 90, 31, 0.08);

      span {
        color: @settings-neon;
        font-weight: 600;
      }
    }
  }
}

// ====== LOGOUT POPUP ======
.logout-popup {
  background: @settings-card !important;
  border-radius: 16px !important;

  &__content {
    padding: 28px 24px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 24px;
  }

  &__title {
    font-size: var(--vk-fs-md);
    font-weight: 700;
    color: #ffffff;
    text-align: center;
    margin: 0;
  }

  &__actions {
    display: flex;
    gap: 12px;
    width: 100%;
  }

  &__btn {
    flex: 1;
    height: 42px;
    border-radius: 8px;
    font-size: var(--vk-fs-base);
    font-weight: 700;
    border: none;
    cursor: pointer;

    &--cancel {
      background: rgba(255, 255, 255, 0.1);
      color: #ffffff;
    }

    &--confirm {
      background: var(--vk-danger);
      color: #ffffff;
    }
  }
}

@media (min-width: 769px) {
  .settings-page {
    max-width: var(--vk-pc-col);
    margin: 0 auto;
  }
}
</style>
